import { computed, inject, Injectable, signal, Type } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { IndicationsListComponent } from '../components/indications-list/indications-list.component';
// HRS-3045: solo el tipo. Este servicio es root y lo inyecta el chatbot (eager); el import
// como valor metía new-details + leaflet + chart.js en el bundle inicial. Ver _loadDetailsPage().
import type { NewDetailsPage } from 'src/app/pages/new-details/new-details.page';
import { BottomSheetConfig, MatDialogDetailsData } from 'src/app/interfaces/mat.interface';
import { Point, Transfers } from '../../../interfaces/custom-route.interface';
import { MatDialogService } from 'src/app/services';
import { MatDialogRef } from '@angular/material/dialog';
import { CustomRouteService } from '../../../services';

@Injectable({ providedIn: 'root' })

export class IndicatorsService {
  private _bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private _matDialogService = inject(MatDialogService);
  private _customRouteService = inject(CustomRouteService);
  
  public bottomSheetRef = signal<MatBottomSheetRef | null>(null);
  public dialogRef = signal<MatDialogRef<NewDetailsPage> | null>(null);
  private isOpening = signal<boolean>(false);
  private detailsId = signal<number>(null);

  private intructions = signal<string>(undefined);
  private timeRemaining = signal<number>(undefined);
  private stepProgress = signal<number>(undefined);
  private nextItem = signal<string>(undefined);
  private sign = signal<string>(undefined);

  public getInstructions = computed<string>(() => this.intructions());
  public getTimeRemaining = computed<number>(() => this.timeRemaining());
  public getStepProgress = computed<number>(() => this.stepProgress());
  public getNextItem = computed<string>(() => this.nextItem());
  public getSign = computed<string>(() => this.sign());

  // ----- DETAILS ----------------------------------------------------------------------------------------------------------------
    /** La página de detalles se descarga bajo demanda (chunk propio) al abrir el detalle. */
    private async _loadDetailsPage(): Promise<Type<NewDetailsPage>> {
      const m = await import('src/app/pages/new-details/new-details.page');
      return m.NewDetailsPage;
    }

    public async openBottomSheetDetailsMobile(item: Point): Promise<void> {
      if (this.isOpening())
        return;

      // El guard se activa ANTES del await para que un doble toque no abra dos hojas; si el
      // chunk falla se libera y se relanza para que ChunkErrorHandler gestione la recarga.
      this.isOpening.set(true);
      let component: Type<NewDetailsPage>;
      try {
        component = await this._loadDetailsPage();
      } catch (e) {
        this.isOpening.set(false);
        throw e;
      }

      const bottomSheetConfig: BottomSheetConfig<NewDetailsPage, any> = {
        component,
        data: {
          id: item.id,
          type: item.type,
          isValid: item.isValid,
          fromPointPlannerCustomRouteMobile: true,
          isModal: true,
          fromLocation: this._customRouteService.getCoordinates()
        },
        panelClass: 'details-bottom-sheet--mobile',
      }

      this.bottomSheetRef.set(this._bottomSheet.open(component, bottomSheetConfig));
      this.bottomSheetRef().afterDismissed().subscribe(() => {
          this.bottomSheetRef.set(null);
      });
      this.bottomSheetRef().afterOpened().subscribe(() => {
        this.isOpening.set(false);
      });
    }

    public async openDialogDetailsDesktop(item: Point): Promise<void> {
      if (this.isOpening())
        return;

      if (this.detailsId() === item.id && this._matDialogService.currentDialogRef()){
        this._matDialogService.closeMatDialog(this._matDialogService.currentDialogRef());
        this.detailsId.set(null);
        return;
      }

      if (this._matDialogService.currentDialogRef()){
        this._matDialogService.closeMatDialog(this._matDialogService.currentDialogRef());
        return;
      }

      const component = await this._loadDetailsPage();
      await this._matDialogService.openMatDialog<NewDetailsPage, MatDialogDetailsData>({
        component,
        data: {
          id: item.id,
          type: item.type,
          isValid: item.isValid,
          fromMatDialog: true,
        },
        panelClass: 'details-mat-dialog--desktop',
      });
    }

    async loadDetailsDesktop(item: Point): Promise<void> {
      this.isOpening.set(true);
      let component: Type<NewDetailsPage>;
      try {
        component = await this._loadDetailsPage();
      } catch (e) {
        this.isOpening.set(false);
        throw e;
      }

      const bottomSheetConfig: BottomSheetConfig<NewDetailsPage, any> = {
        component,
        data: {
          id: item.id,
          type: item.type,
          isValid: item.isValid,
          fromPointPlannerCustomRouteDesktop: true,
          isModal: true
        },
        panelClass: 'details-bottom-sheet--desktop',
      }
      this._bottomSheet.open(component, bottomSheetConfig);
      this._bottomSheet._openedBottomSheetRef.afterOpened().subscribe(() => {
        this.isOpening.set(false);
      })

      this.detailsId.set(item.id);
    }


  // ----- TRANSFERS ----------------------------------------------------------------------------------------------------------------
    public openBottomSheetTransfersMobile(transfers: Transfers[]): void {
      if (this.isOpening())
        return;

      const bottomSheetConfig: BottomSheetConfig<IndicationsListComponent, any> = {
        component: IndicationsListComponent,
        data: {
            transfers: transfers,
            fromPointPlannerCustomRouteMobile: true,
        },
        panelClass: 'indications-list-bottom-sheet--mobile',
      }

      this.isOpening.set(true);
      this.bottomSheetRef.set(this._bottomSheet.open(IndicationsListComponent, bottomSheetConfig));
      this.bottomSheetRef().afterDismissed().subscribe(() => {
          this.bottomSheetRef.set(null);
      });
      this.bottomSheetRef().afterOpened().subscribe(() => {
        this.isOpening.set(false);
      });
    }

    public openBottomSheetTransfersDesktop(transfers: Transfers[]): void {
      if (this.isOpening())
        return;

      if (this._bottomSheet._openedBottomSheetRef){
        this._bottomSheet?.dismiss();
        return;
      }
      this.loadTransfersDesktop(transfers);
    }

    loadTransfersDesktop(transfers: Transfers[]): void{
      this.isOpening.set(true);
      const bottomSheetConfig: BottomSheetConfig<IndicationsListComponent, any> = {
        component: IndicationsListComponent,
        data: {
            transfers: transfers,
            fromPointPlannerCustomRouteDesktop: true,
        },
        panelClass: 'indications-list-bottom-sheet--desktop',
      }
      this._bottomSheet.open(IndicationsListComponent, bottomSheetConfig);
      this._bottomSheet._openedBottomSheetRef.afterOpened().subscribe(() => {
        this.isOpening.set(false);
      });
    }

// ----- AUXILIAR FUNCTIONS ----------------------------------------------------------------------------------------------------------------
    public closeAll(): void{
      this._bottomSheet?.dismiss();
    }

    public resetInstructions(): void{
      this.intructions.set(null);
      this.sign.set(null);
      this.nextItem.set(null);
      this.stepProgress.set(null);
      this.timeRemaining.set(null);
    }

    public setInstructions(currentStep: Transfers, nextPoint: string = undefined): void{
      const sign = currentStep?.mode === 'walking' ? currentStep.iconName : currentStep?.mode?.toLowerCase();
      this.intructions.set(currentStep?.instruction);
      this.sign.set(sign);
      this.nextItem.set(nextPoint);
    }

    public setTimeRemainig(time: number): void{
      this.timeRemaining.set(time);
    }

    public setStepProgress(progress: number): void{
      this.stepProgress.set(progress)
    }

}
